/**
 * Aurora Canvas 独立 API 客户端
 * 不修改现有 utils/api.ts
 */

import { useAuthStore } from '../stores/auth'

const API_BASE = 'https://cloudflare-mutimodal-backend.steven-mei0814.workers.dev'

export interface PromptFields {
  subject?: string
  action?: string
  environment?: string
  composition?: string
  lighting?: string
  camera?: string
  color?: string
  medium?: string
  style?: string
  mood?: string
  quality?: string
}

export interface CanvasSummary {
  id: string
  name: string
  created_at: number
  updated_at: number
}

export interface CanvasDetail extends CanvasSummary {
  data: unknown
}

export interface CanvasGenerateResult {
  id: string
  url: string
  prompt: string
  model: string
  canvas_id: string | null
  width: number
  height: number
  created_at: number
}

interface ApiEnvelope<T> {
  success: boolean
  data?: T
  error?: string
}

function authHeader(): Record<string, string> {
  const { token } = useAuthStore.getState()
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) headers['Authorization'] = `Bearer ${token}`
  return headers
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...authHeader(),
      ...(init.headers || {}),
    },
  })

  let json: ApiEnvelope<T>
  try {
    json = (await response.json()) as ApiEnvelope<T>
  } catch (e) {
    throw new Error(`Invalid JSON from ${path}: ${response.status}`)
  }

  if (!response.ok || !json.success) {
    throw new Error(json.error || `Request failed: ${response.status}`)
  }
  return json.data as T
}

export const canvasApi = {
  list: () => request<{ items: CanvasSummary[] }>('/api/canvas', { method: 'GET' }),

  get: (id: string) => request<CanvasDetail>(`/api/canvas/${encodeURIComponent(id)}`, { method: 'GET' }),

  create: (name: string, data: unknown = {}) =>
    request<CanvasDetail>('/api/canvas', {
      method: 'POST',
      body: JSON.stringify({ name, data }),
    }),

  update: (id: string, patch: { name?: string; data?: unknown }) =>
    request<{ id: string; name: string; updated_at: number }>(
      `/api/canvas/${encodeURIComponent(id)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(patch),
      },
    ),

  remove: (id: string) =>
    request<{ id: string }>(`/api/canvas/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  assemble: (fields: PromptFields) =>
    request<{ prompt: string; token_count_estimate: number; warnings: string[] }>(
      '/api/canvas/prompt/assemble',
      {
        method: 'POST',
        body: JSON.stringify({ fields }),
      },
    ),

  optimize: (fields: PromptFields) =>
    request<{ optimized: string; baseline: string; token_count_estimate: number }>(
      '/api/canvas/prompt/optimize',
      {
        method: 'POST',
        body: JSON.stringify({ fields }),
      },
    ),

  generate: (params: {
    fields: PromptFields
    prompt?: string
    canvas_id?: string
    seed?: number
    model?: string
  }) =>
    request<CanvasGenerateResult>('/api/canvas/generate', {
      method: 'POST',
      body: JSON.stringify(params),
    }),
}
